import { FC, useState, useEffect } from 'react';
import { Box, Chip, FormHelperText } from '@mui/material';

import { useForm } from '@carcareers/contexts';

interface RadioInputProps {
  name: string;
  options: string[];
  multiple?: boolean;
  orientation?: 'horizontal' | 'vertical';
  disabledOptions?: string[];
}

export const RadioInput: FC<RadioInputProps> = ({
  name,
  options,
  disabledOptions,
  multiple = false,
  orientation = 'vertical',
}) => {
  const { getData, setData, getErrors } = useForm();
  const [selected, setSelected] = useState<string[]>([]);

  const handleChange = (option: string) => {
    if (disabledOptions?.includes(option)) {
      return;
    }

    if (multiple) {
      if (selected.includes(option)) {
        setData(
          name,
          selected.filter((s) => s !== option),
        );
      } else {
        setData(name, [...selected, option]);
      }
    } else {
      setData(name, option);
    }
  };

  const errors = getErrors(name);
  
  useEffect(() => {
    const data = getData<string | string[]>(name);

    if (data) {
      setSelected(Array.isArray(data) ? data : [data]);
    }
  }, [getData, name]);

  return (
    <Box sx={{ display: 'flex', flexDirection: orientation === 'vertical' ? 'column' : 'row', gap: 2 }}>
      {options.map((option) => (
        <Chip
          key={`radio-${option}`}
          label={option.charAt(0).toUpperCase() + option.slice(1)}
          onClick={() => handleChange(option)}
          color={selected.includes(option) ? 'primary' : 'default'}
          variant={selected.includes(option) ? 'filled' : 'outlined'}
          sx={{
            opacity: disabledOptions?.includes(option) ? 0.5 : 1,
            cursor: disabledOptions?.includes(option) ? 'not-allowed' : 'pointer',
            borderRadius: 1,
            height: 56,
          }}
        />
      ))}
      {errors && <FormHelperText error>{errors}</FormHelperText>}
    </Box>
  );
};
